import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ChartBar,
  Palette,
  Users,
  Images,
  Gear,
  ArrowLeft,
  Trash,
  SealCheck,
  Prohibit,
  Crown,
  PushPin,
  Star,
  Warning,
  FloppyDisk,
  UploadSimple,
  Shapes,
  Plus,
  ArrowUp,
  ArrowDown,
  X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: ChartBar },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "disciplines", label: "Disciplinas", icon: Shapes },
  { key: "users", label: "Usuarios", icon: Users },
  { key: "posts", label: "Contenido", icon: Images },
  { key: "config", label: "Configuración", icon: Gear },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F4F4F5] grid place-items-center font-mono text-[11px] uppercase tracking-[0.3em] text-[#A1A1AA]">
        cargando…
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      data-testid="admin-panel"
      className="min-h-screen bg-[#050505] text-[#F4F4F5] flex flex-col"
    >
      <header className="border-b border-[#27272A] px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[10px] text-[#A1A1AA] hover:text-white tracking-[0.22em] uppercase"
          >
            <ArrowLeft size={12} /> Volver al sitio
          </Link>
          <div className="font-display text-xl font-black uppercase tracking-tight">
            / SUPER<span className="text-[#FF3E00]">ADMIN</span>
          </div>
        </div>
        <div className="font-mono text-[10px] tracking-[0.22em] text-[#00FF66]">
          ● @{user.handle}
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[220px_1fr] min-h-0">
        <aside className="border-r border-[#27272A] p-3 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] border ${
                  active
                    ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                    : "border-transparent text-[#A1A1AA] hover:bg-[#121212] hover:text-white"
                }`}
              >
                <Icon size={14} weight={active ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </aside>
        <main className="min-w-0 overflow-auto p-4 md:p-8">
          {tab === "dashboard" && <DashboardTab />}
          {tab === "branding" && <BrandingTab />}
          {tab === "disciplines" && <DisciplinesTab />}
          {tab === "users" && <UsersTab />}
          {tab === "posts" && <PostsTab />}
          {tab === "config" && <ConfigTab />}
        </main>
      </div>
    </div>
  );
}

/* ================================================================== */
/* DASHBOARD                                                           */
/* ================================================================== */

function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const totals = data?.totals || {};
  const growth = data?.growth_daily || [];
  const max = Math.max(1, ...growth.map((g) => g.count));

  return (
    <div data-testid="admin-dashboard" className="space-y-8 max-w-5xl">
      <SectionHeader num="01" title="Dashboard · métricas del ecosistema" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Stat label="Artistas" value={totals.users} accent />
        <Stat label="Verificados" value={totals.users_verified} />
        <Stat label="Baneados" value={totals.users_banned} />
        <Stat label="Posts" value={totals.posts} />
        <Stat label="Mensajes" value={totals.messages} />
        <Stat label="Follows" value={totals.follows} />
        <Stat label="Notificaciones" value={totals.notifications} />
      </div>

      <div className="border border-[#27272A] p-5">
        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-4">
          // CRECIMIENTO · REGISTROS POR DÍA
        </div>
        {growth.length === 0 ? (
          <div className="font-mono text-[11px] text-[#525252]">Sin datos aún.</div>
        ) : (
          <div className="flex items-end gap-2 h-40">
            {growth.map((g) => (
              <div key={g.day} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-[#FF3E00]"
                  style={{ height: `${(g.count / max) * 100}%`, minHeight: 4 }}
                  title={`${g.day} · ${g.count}`}
                />
                <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider">
                  {g.day.slice(5)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className={`border p-4 ${
        accent ? "border-[#FF3E00] bg-[#FF3E00]/5" : "border-[#27272A] bg-[#0a0a0a]"
      }`}
    >
      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
        {label.toUpperCase()}
      </div>
      <div
        className={`font-display font-black text-4xl tracking-tight mt-2 ${
          accent ? "text-[#FF3E00]" : ""
        }`}
      >
        {(value ?? 0).toLocaleString("es")}
      </div>
    </div>
  );
}

/* ================================================================== */
/* BRANDING                                                            */
/* ================================================================== */

function BrandingTab() {
  const { settings, palettes, refresh } = useBrand();
  const [form, setForm] = useState({
    site_name: "",
    tagline: "",
    footer_text: "",
    logo_url: "",
    palette: "blaze-orange",
    default_theme: "dark",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!settings) return;
    setForm({
      site_name: settings.site_name || "",
      tagline: settings.tagline || "",
      footer_text: settings.footer_text || "",
      logo_url: settings.logo_url || "",
      palette: settings.palette || "blaze-orange",
      default_theme: settings.default_theme || "dark",
    });
  }, [settings]);

  const onSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.patch("/admin/settings", form);
      await refresh();
      setMsg("Guardado · aplicado en vivo ✓");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onUploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: data.url }));
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-branding" className="space-y-8 max-w-4xl">
      <SectionHeader num="02" title="Branding · identidad visual del sitio" />

      {/* Palette picker */}
      <div>
        <Label>// PALETA DE ACENTO</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          {Object.entries(palettes).map(([key, meta]) => {
            const active = form.palette === key;
            return (
              <button
                key={key}
                data-testid={`palette-${key}`}
                onClick={() => setForm((f) => ({ ...f, palette: key }))}
                className={`border p-3 text-left transition-colors ${
                  active
                    ? "border-[#FF3E00] bg-[#FF3E00]/5"
                    : "border-[#27272A] hover:border-[#F4F4F5]"
                }`}
              >
                <div
                  className="h-14 w-full mb-3"
                  style={{ backgroundColor: meta.accent }}
                />
                <div className="font-display font-bold text-sm">{meta.label}</div>
                <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider mt-0.5">
                  {meta.accent}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme */}
      <div>
        <Label>// TEMA POR DEFECTO</Label>
        <div className="grid grid-cols-2 gap-3 mt-3 max-w-md">
          {[
            ["dark", "Dark · brutalist"],
            ["editorial", "Editorial · light"],
          ].map(([k, l]) => {
            const active = form.default_theme === k;
            return (
              <button
                key={k}
                onClick={() => setForm((f) => ({ ...f, default_theme: k }))}
                className={`border px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] ${
                  active
                    ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                    : "border-[#27272A] text-[#A1A1AA] hover:text-white"
                }`}
              >
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Text fields */}
      <div className="grid md:grid-cols-2 gap-4">
        <TextField
          label="// NOMBRE DEL SITIO"
          value={form.site_name}
          onChange={(v) => setForm((f) => ({ ...f, site_name: v }))}
        />
        <TextField
          label="// TAGLINE"
          value={form.tagline}
          onChange={(v) => setForm((f) => ({ ...f, tagline: v }))}
        />
      </div>
      <TextField
        label="// TEXTO DEL PIE"
        value={form.footer_text}
        onChange={(v) => setForm((f) => ({ ...f, footer_text: v }))}
      />

      {/* Logo upload */}
      <div>
        <Label>// LOGO (opcional · si vacío usamos el logotipo de texto)</Label>
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <div className="h-16 w-44 bg-[#0a0a0a] border border-[#27272A] flex items-center justify-center">
            {form.logo_url ? (
              <img
                src={
                  form.logo_url.startsWith("/api/")
                    ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`
                    : form.logo_url
                }
                alt="logo"
                className="max-h-14 max-w-40 object-contain"
              />
            ) : (
              <span className="font-display text-xl font-black uppercase">
                {form.site_name || "ELARTE/NET"}
              </span>
            )}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3E00] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em]">
            <UploadSimple size={14} /> Subir nuevo
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onUploadLogo(e.target.files?.[0])}
            />
          </label>
          {form.logo_url ? (
            <button
              onClick={() => setForm((f) => ({ ...f, logo_url: "" }))}
              className="font-mono text-[11px] text-[#FF8899] hover:text-[#FF3E00] tracking-wider"
            >
              Quitar logo
            </button>
          ) : null}
        </div>
      </div>

      <SaveBar saving={saving} onSave={onSave} msg={msg} />
    </div>
  );
}

/* ================================================================== */
/* USERS                                                                */
/* ================================================================== */

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setUsers(data.users || []);
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const toggle = async (u, field) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, {
        [field]: !u[field],
      });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data.user : x)));
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a @${u.handle} definitivamente? Esta acción NO se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "user" : "admin";
    if (!window.confirm(`Cambiar rol de @${u.handle} a ${next.toUpperCase()}?`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role: next });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data.user : x)));
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-users" className="space-y-6 max-w-6xl">
      <SectionHeader num="04" title="Usuarios · moderación y verificación" />

      <input
        data-testid="admin-users-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="buscar nombre · handle · email..."
        className="w-full max-w-md bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2.5 font-mono text-sm"
      />

      {msg ? (
        <div className="font-mono text-[11px] text-[#FF8899] border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2">
          {msg}
        </div>
      ) : null}

      <div className="border border-[#27272A]">
        <div className="grid grid-cols-[1fr_180px_100px_140px] gap-3 px-4 py-2 border-b border-[#27272A] font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] bg-[#0a0a0a]">
          <span>Usuario</span>
          <span>Email</span>
          <span>Rol</span>
          <span className="text-right">Acciones</span>
        </div>
        {loading ? (
          <div className="p-6 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
            cargando…
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
            sin resultados.
          </div>
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-[1fr_180px_100px_140px] gap-3 px-4 py-3 border-b border-[#27272A] items-center hover:bg-[#121212]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 bg-[#121212] border border-[#27272A] overflow-hidden shrink-0">
                  {u.photo ? (
                    <img src={u.photo} alt="" className="h-9 w-9 object-cover" />
                  ) : null}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm flex items-center gap-1">
                    {u.name}
                    {u.verified ? (
                      <SealCheck size={11} weight="fill" className="text-[#FF3E00]" />
                    ) : null}
                    {u.banned ? (
                      <span className="ml-1 font-mono text-[9px] bg-[#FF0033] text-white px-1">
                        BAN
                      </span>
                    ) : null}
                  </div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] truncate">
                    @{u.handle}
                  </div>
                </div>
              </div>
              <div className="font-mono text-[10px] text-[#A1A1AA] truncate">
                {u.email}
              </div>
              <div className="font-mono text-[10px]">
                {u.role === "admin" ? (
                  <span className="inline-flex items-center gap-1 bg-[#FF3E00] text-white px-1.5 py-0.5 uppercase tracking-wider">
                    <Crown size={9} weight="fill" /> ADMIN
                  </span>
                ) : (
                  <span className="text-[#A1A1AA] uppercase tracking-wider">USER</span>
                )}
              </div>
              <div className="flex items-center justify-end gap-1">
                <ActionBtn
                  title={u.verified ? "Quitar verificación" : "Verificar"}
                  onClick={() => toggle(u, "verified")}
                  icon={SealCheck}
                  active={u.verified}
                  testid={`verify-${u.handle}`}
                />
                <ActionBtn
                  title={u.banned ? "Desbanear" : "Banear"}
                  onClick={() => toggle(u, "banned")}
                  icon={Prohibit}
                  active={u.banned}
                  danger
                  testid={`ban-${u.handle}`}
                />
                <ActionBtn
                  title={u.role === "admin" ? "Quitar admin" : "Promover a admin"}
                  onClick={() => toggleRole(u)}
                  icon={Crown}
                  active={u.role === "admin"}
                  testid={`role-${u.handle}`}
                />
                <ActionBtn
                  title="Eliminar"
                  onClick={() => remove(u)}
                  icon={Trash}
                  danger
                  testid={`del-${u.handle}`}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, onClick, title, active, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={title}
      className={`h-8 w-8 inline-flex items-center justify-center border transition-colors ${
        active && danger
          ? "bg-[#FF0033] text-white border-[#FF0033]"
          : active
          ? "bg-[#FF3E00] text-white border-[#FF3E00]"
          : danger
          ? "border-[#27272A] text-[#A1A1AA] hover:border-[#FF0033] hover:text-[#FF0033]"
          : "border-[#27272A] text-[#A1A1AA] hover:border-[#FF3E00] hover:text-[#FF3E00]"
      }`}
    >
      <Icon size={13} weight={active ? "fill" : "regular"} />
    </button>
  );
}

/* ================================================================== */
/* POSTS                                                                */
/* ================================================================== */

function PostsTab() {
  const { settings, refresh } = useBrand();
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const pinned = settings?.pinned_post_ids || [];
  const featured = settings?.featured_post_ids || [];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts", { params: q ? { q } : {} });
      setPosts(data.posts || []);
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const togglePin = async (p) => {
    const next = pinned.includes(p.id)
      ? pinned.filter((x) => x !== p.id)
      : [p.id, ...pinned].slice(0, 5);
    await api.patch("/admin/settings", { pinned_post_ids: next });
    await refresh();
  };

  const toggleFeature = async (p) => {
    const next = featured.includes(p.id)
      ? featured.filter((x) => x !== p.id)
      : [p.id, ...featured].slice(0, 10);
    await api.patch("/admin/settings", { featured_post_ids: next });
    await refresh();
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar post de @${p.author?.handle}?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      await refresh();
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-posts" className="space-y-6 max-w-6xl">
      <SectionHeader num="05" title="Contenido · posts, destacados y fijados" />

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="buscar caption · tag..."
        className="w-full max-w-md bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2.5 font-mono text-sm"
      />
      {msg ? (
        <div className="font-mono text-[11px] text-[#FF8899] border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2">
          {msg}
        </div>
      ) : null}

      {loading ? (
        <div className="p-6 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
          cargando…
        </div>
      ) : posts.length === 0 ? (
        <div className="p-6 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
          sin posts.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((p) => {
            const isPinned = pinned.includes(p.id);
            const isFeatured = featured.includes(p.id);
            return (
              <div
                key={p.id}
                className="border border-[#27272A] bg-[#0a0a0a] flex flex-col"
              >
                {p.media_url ? (
                  <div className="aspect-[4/3] bg-[#121212] overflow-hidden">
                    <img
                      src={p.media_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[#050505] grid place-items-center p-4">
                    <p className="font-display text-sm text-center line-clamp-3">
                      {p.caption}
                    </p>
                  </div>
                )}
                <div className="p-3 border-t border-[#27272A] flex-1 flex flex-col">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-6 w-6 bg-[#121212] border border-[#27272A] overflow-hidden shrink-0">
                      {p.author?.photo ? (
                        <img src={p.author.photo} alt="" className="h-6 w-6 object-cover" />
                      ) : null}
                    </div>
                    <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                      @{p.author?.handle}
                    </span>
                    <span className="ml-auto font-mono text-[10px] text-[#FF3E00]">
                      ♥ {p.likes || 0}
                    </span>
                  </div>
                  {p.caption ? (
                    <p className="text-[12px] text-[#E5E5E5] mt-2 line-clamp-2">
                      {p.caption}
                    </p>
                  ) : null}
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#27272A]">
                    <ActionBtn
                      title={isPinned ? "Desfijar" : "Fijar en inicio (máx 5)"}
                      onClick={() => togglePin(p)}
                      icon={PushPin}
                      active={isPinned}
                      testid={`pin-${p.id.slice(0, 8)}`}
                    />
                    <ActionBtn
                      title={isFeatured ? "Quitar destacado" : "Destacar (máx 10)"}
                      onClick={() => toggleFeature(p)}
                      icon={Star}
                      active={isFeatured}
                      testid={`feat-${p.id.slice(0, 8)}`}
                    />
                    <ActionBtn
                      title="Eliminar"
                      onClick={() => remove(p)}
                      icon={Trash}
                      danger
                      testid={`delpost-${p.id.slice(0, 8)}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* CONFIG                                                              */
/* ================================================================== */

function ConfigTab() {
  const { settings, refresh } = useBrand();
  const [form, setForm] = useState({
    banner_active: false,
    banner_text: "",
    banner_link: "",
    registration_mode: "open",
    trending_tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!settings) return;
    setForm({
      banner_active: !!settings.banner_active,
      banner_text: settings.banner_text || "",
      banner_link: settings.banner_link || "",
      registration_mode: settings.registration_mode || "open",
      trending_tags: settings.trending_tags || [],
    });
  }, [settings]);

  const onSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.patch("/admin/settings", form);
      await refresh();
      setMsg("Guardado · aplicado en vivo ✓");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (!t) return;
    setForm((f) =>
      f.trending_tags.includes(`#${t}`)
        ? f
        : { ...f, trending_tags: [...f.trending_tags, `#${t}`] }
    );
    setTagInput("");
  };

  return (
    <div data-testid="admin-config" className="space-y-8 max-w-4xl">
      <SectionHeader num="06" title="Configuración · banners y registros" />

      {/* Banner */}
      <div className="border border-[#27272A] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Label>// BANNER GLOBAL</Label>
          <button
            data-testid="banner-active-toggle"
            onClick={() =>
              setForm((f) => ({ ...f, banner_active: !f.banner_active }))
            }
            className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 ${
              form.banner_active
                ? "bg-[#FF3E00] text-white"
                : "border border-[#27272A] text-[#A1A1AA]"
            }`}
          >
            {form.banner_active ? "ACTIVO ●" : "INACTIVO ○"}
          </button>
        </div>
        <TextField
          label="// TEXTO"
          value={form.banner_text}
          onChange={(v) => setForm((f) => ({ ...f, banner_text: v }))}
          placeholder="Ej. Nuevo festival en Matadero · 12 de julio"
        />
        <TextField
          label="// ENLACE (opcional)"
          value={form.banner_link}
          onChange={(v) => setForm((f) => ({ ...f, banner_link: v }))}
          placeholder="/convocatorias o https://…"
        />
      </div>

      {/* Registration mode */}
      <div>
        <Label>// MODO DE REGISTRO</Label>
        <div className="grid grid-cols-3 gap-3 mt-3 max-w-xl">
          {[
            ["open", "Abierto", "Cualquiera puede crear cuenta"],
            ["invite", "Invitación", "Solo con código/invitación"],
            ["closed", "Cerrado", "Registros bloqueados"],
          ].map(([k, l, d]) => {
            const active = form.registration_mode === k;
            return (
              <button
                key={k}
                data-testid={`regmode-${k}`}
                onClick={() => setForm((f) => ({ ...f, registration_mode: k }))}
                className={`border p-4 text-left transition-colors ${
                  active
                    ? "border-[#FF3E00] bg-[#FF3E00]/5"
                    : "border-[#27272A] hover:border-[#F4F4F5]"
                }`}
              >
                <div className="font-display font-bold text-sm">{l}</div>
                <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider mt-1">
                  {d.toUpperCase()}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Trending tags */}
      <div>
        <Label>// HASHTAGS TENDENCIA</Label>
        <div className="mt-3 flex items-center gap-2 max-w-md">
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
            placeholder="ej. matadero2026"
            className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2 font-mono text-sm"
          />
          <button
            onClick={addTag}
            className="font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-2 border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black"
          >
            + Añadir
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {form.trending_tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] bg-[#FF3E00] text-white px-2 py-1"
            >
              {t}
              <button
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    trending_tags: f.trending_tags.filter((x) => x !== t),
                  }))
                }
                className="hover:text-white"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      <SaveBar saving={saving} onSave={onSave} msg={msg} />
    </div>
  );
}

/* ================================================================== */
/* DISCIPLINES                                                         */
/* ================================================================== */

function DisciplinesTab() {
  const { disciplines: live, refresh } = useBrand();
  const [items, setItems] = useState(live || []);
  const [editing, setEditing] = useState(null); // slug or "__new__"
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    setItems(live || []);
  }, [live]);

  const reload = async () => {
    await refresh();
  };

  const startNew = () => {
    setEditing("__new__");
    setDraft({
      slug: "",
      name: "",
      tag: "#",
      order: (items.length || 0) + 1,
      sub_categories: [],
    });
  };

  const startEdit = (d) => {
    setEditing(d.slug);
    setDraft({ ...d, sub_categories: [...(d.sub_categories || [])] });
  };

  const close = () => {
    setEditing(null);
    setDraft(null);
    setMsg("");
  };

  const save = async () => {
    setMsg("");
    try {
      if (editing === "__new__") {
        await api.post("/admin/disciplines", draft);
      } else {
        await api.patch(`/admin/disciplines/${editing}`, {
          name: draft.name,
          tag: draft.tag,
          order: draft.order,
          sub_categories: draft.sub_categories,
        });
      }
      await reload();
      close();
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminar disciplina "${d.name}"? No se podrá si hay artistas usándola.`)) return;
    try {
      await api.delete(`/admin/disciplines/${d.slug}`);
      await reload();
    } catch (e) {
      setMsg(formatApiError(e));
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx];
    const b = items[j];
    // Swap orders and persist
    await Promise.all([
      api.patch(`/admin/disciplines/${a.slug}`, { order: b.order }),
      api.patch(`/admin/disciplines/${b.slug}`, { order: a.order }),
    ]);
    await reload();
  };

  // Subcategory editors in the drawer
  const addSub = () => {
    setDraft((d) => ({
      ...d,
      sub_categories: [
        ...d.sub_categories,
        { slug: `sub-${d.sub_categories.length + 1}`, name: "Nueva subcategoría" },
      ],
    }));
  };

  const updateSub = (i, field, v) => {
    setDraft((d) => {
      const next = d.sub_categories.slice();
      next[i] = { ...next[i], [field]: v };
      return { ...d, sub_categories: next };
    });
  };

  const removeSub = (i) => {
    setDraft((d) => ({
      ...d,
      sub_categories: d.sub_categories.filter((_, k) => k !== i),
    }));
  };

  const moveSub = (i, dir) => {
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.sub_categories.length) return d;
      const next = d.sub_categories.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, sub_categories: next };
    });
  };

  return (
    <div data-testid="admin-disciplines" className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <SectionHeader num="03" title="Disciplinas · las artes y sus subcategorías" />
        <button
          data-testid="discipline-new"
          onClick={startNew}
          className="shrink-0 btn-brutal inline-flex items-center gap-2 bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-4 py-2.5 hover:bg-black hover:text-[#FF3E00] border border-[#FF3E00]"
        >
          <Plus size={14} weight="bold" /> Nueva
        </button>
      </div>
      <p className="font-mono text-[10px] text-[#A1A1AA] tracking-wider max-w-2xl">
        Haz clic en cualquier chip de subcategoría o en el engranaje para abrir el editor completo. Reordena con las flechas. El slug no se puede cambiar una vez creada la disciplina.
      </p>

      {msg ? (
        <div className="font-mono text-[11px] text-[#FF8899] border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2">
          {msg}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((d, i) => (
          <div
            key={d.slug}
            data-testid={`disc-card-${d.slug}`}
            className="group relative border border-[#27272A] bg-[#0a0a0a] hover:border-[#FF3E00] transition-colors overflow-hidden"
          >
            {/* Accent stripe */}
            <div className="absolute top-0 left-0 w-1 h-full bg-[#FF3E00]" />

            {/* Header: number + name + hashtag */}
            <div className="p-5 pl-7 border-b border-[#27272A]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-1">
                    DISCIPLINA · {String(d.order).padStart(2, "0")}
                  </div>
                  <h3 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">
                    {d.name}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                    <span className="text-[#FF3E00]">{d.tag}</span>
                    <span className="text-[#525252]">·</span>
                    <span>/{d.slug}</span>
                  </div>
                </div>

                {/* Quick reorder (visible only on hover) */}
                <div className="flex flex-col gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                  <ActionBtn
                    icon={ArrowUp}
                    title="Subir"
                    onClick={() => move(i, -1)}
                    testid={`disc-up-${d.slug}`}
                  />
                  <ActionBtn
                    icon={ArrowDown}
                    title="Bajar"
                    onClick={() => move(i, 1)}
                    testid={`disc-down-${d.slug}`}
                  />
                </div>
              </div>
            </div>

            {/* Subcategories as clickable chips (open drawer) */}
            <div className="p-5 pl-7 min-h-[110px]">
              <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                SUBCATEGORÍAS · {(d.sub_categories || []).length}
              </div>
              {d.sub_categories && d.sub_categories.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {d.sub_categories.map((sc) => (
                    <button
                      key={sc.slug}
                      onClick={() => startEdit(d)}
                      title="Editar"
                      className="font-mono text-[10px] uppercase tracking-[0.15em] border border-[#27272A] bg-[#121212] text-[#E5E5E5] hover:border-[#FF3E00] hover:text-[#FF3E00] px-2.5 py-1 transition-colors"
                    >
                      {sc.name}
                    </button>
                  ))}
                  <button
                    onClick={() => startEdit(d)}
                    title="Añadir subcategoría"
                    className="font-mono text-[10px] uppercase tracking-[0.15em] border border-dashed border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white px-2.5 py-1"
                  >
                    + añadir
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => startEdit(d)}
                  className="w-full border-2 border-dashed border-[#27272A] hover:border-[#FF3E00] py-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[#525252] hover:text-[#FF3E00] transition-colors"
                >
                  + Añadir primera subcategoría
                </button>
              )}
            </div>

            {/* Footer: actions */}
            <div className="px-5 pl-7 py-3 border-t border-[#27272A] bg-[#050505] flex items-center justify-between">
              <span className="font-mono text-[9px] text-[#525252] tracking-[0.22em]">
                EDITAR COMPLETA →
              </span>
              <div className="flex items-center gap-1">
                <ActionBtn
                  icon={Gear}
                  title="Editar disciplina y subcategorías"
                  onClick={() => startEdit(d)}
                  testid={`disc-edit-${d.slug}`}
                />
                <ActionBtn
                  icon={Trash}
                  title="Eliminar"
                  onClick={() => remove(d)}
                  danger
                  testid={`disc-del-${d.slug}`}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add-new card */}
        <button
          data-testid="discipline-new-card"
          onClick={startNew}
          className="border-2 border-dashed border-[#27272A] hover:border-[#FF3E00] text-[#525252] hover:text-[#FF3E00] bg-[#050505] min-h-[240px] flex flex-col items-center justify-center gap-3 transition-colors group"
        >
          <div className="h-14 w-14 border border-current rounded-none flex items-center justify-center">
            <Plus size={22} weight="bold" />
          </div>
          <div className="font-display font-black text-xl uppercase tracking-tight">
            Nueva disciplina
          </div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase">
            + subcategorías
          </div>
        </button>
      </div>

      {editing && draft ? (
        <div
          data-testid="discipline-drawer"
          className="fixed inset-0 z-[70] bg-black/60 flex items-start justify-end"
          onClick={(e) => e.target === e.currentTarget && close()}
        >
          <div className="h-full w-full max-w-xl bg-[#050505] border-l border-[#27272A] overflow-auto">
            <header className="sticky top-0 bg-[#050505] border-b border-[#27272A] p-5 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
                  {editing === "__new__" ? "// NUEVA DISCIPLINA" : "// EDITAR"}
                </div>
                <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                  {draft.name || "(sin nombre)"}
                </h2>
              </div>
              <button
                onClick={close}
                className="h-9 w-9 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
              >
                <X size={14} />
              </button>
            </header>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="// NOMBRE"
                  value={draft.name}
                  onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
                />
                <TextField
                  label="// HASHTAG"
                  value={draft.tag}
                  onChange={(v) => setDraft((d) => ({ ...d, tag: v }))}
                />
              </div>
              {editing === "__new__" ? (
                <TextField
                  label="// SLUG (url-friendly)"
                  value={draft.slug}
                  onChange={(v) => setDraft((d) => ({ ...d, slug: v }))}
                />
              ) : (
                <div>
                  <Label>// SLUG (no editable una vez creada)</Label>
                  <div className="font-mono text-[13px] text-[#A1A1AA] mt-2 border border-[#27272A] bg-[#121212] p-2.5">
                    /{draft.slug}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <Label>// SUBCATEGORÍAS ({draft.sub_categories.length})</Label>
                  <button
                    data-testid="subcat-add"
                    onClick={addSub}
                    className="font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white"
                  >
                    + Añadir
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {draft.sub_categories.length === 0 ? (
                    <div className="border-2 border-dashed border-[#27272A] py-8 text-center font-mono text-[10px] text-[#525252] uppercase tracking-[0.22em]">
                      · sin subcategorías aún ·
                    </div>
                  ) : (
                    draft.sub_categories.map((sc, i) => (
                      <div
                        key={i}
                        data-testid={`subcat-card-${i}`}
                        className="border border-[#27272A] bg-[#0a0a0a] p-3 hover:border-[#FF3E00] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em] shrink-0 w-6">
                            {String(i + 1).padStart(2, "0")}
                          </div>
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_140px] gap-2">
                            <div>
                              <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                                // NOMBRE
                              </div>
                              <input
                                data-testid={`subcat-name-${i}`}
                                value={sc.name}
                                onChange={(e) => updateSub(i, "name", e.target.value)}
                                placeholder="Ej. DJ · Electrónica"
                                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-2 py-1.5 font-display text-[14px]"
                              />
                            </div>
                            <div>
                              <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                                // SLUG
                              </div>
                              <input
                                data-testid={`subcat-slug-${i}`}
                                value={sc.slug}
                                onChange={(e) => updateSub(i, "slug", e.target.value)}
                                placeholder="dj-electronica"
                                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-2 py-1.5 font-mono text-[11px] text-[#A1A1AA]"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            <ActionBtn
                              icon={ArrowUp}
                              title="Subir"
                              onClick={() => moveSub(i, -1)}
                            />
                            <ActionBtn
                              icon={ArrowDown}
                              title="Bajar"
                              onClick={() => moveSub(i, 1)}
                            />
                          </div>
                          <ActionBtn
                            icon={Trash}
                            title="Quitar"
                            danger
                            onClick={() => removeSub(i)}
                            testid={`subcat-remove-${i}`}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-[#27272A] flex items-center gap-3">
                <button
                  data-testid="discipline-save"
                  onClick={save}
                  className="btn-brutal inline-flex items-center gap-2 bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-3"
                >
                  <FloppyDisk size={14} weight="fill" /> Guardar
                </button>
                <button
                  onClick={close}
                  className="font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 text-[#A1A1AA] hover:text-white"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

function SectionHeader({ num, title }) {
  return (
    <div>
      <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em] mb-2">
        // SECCIÓN {num}
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tighter">
        {title}
      </h1>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
      {children}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2.5 font-display text-[15px]"
      />
    </div>
  );
}

function SaveBar({ saving, onSave, msg }) {
  return (
    <div className="flex items-center gap-4 border-t border-[#27272A] pt-5">
      <button
        data-testid="admin-save"
        onClick={onSave}
        disabled={saving}
        className={`btn-brutal inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-3 ${
          saving ? "bg-[#121212] text-[#525252]" : "bg-[#FF3E00] text-white"
        }`}
      >
        <FloppyDisk size={14} weight="fill" /> {saving ? "Guardando…" : "Guardar cambios"}
      </button>
      {msg ? (
        <span className="font-mono text-[11px] text-[#00FF66] tracking-wider">
          {msg}
        </span>
      ) : null}
    </div>
  );
}
